import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import axiosInstance from "../../../axiosInstance";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";

const formatDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getNotificationUrl = (notification) =>
  notification.actionUrl || notification.data?.url || "";

const isExternalUrl = (url) => /^https?:\/\//i.test(String(url || ""));

const getNotificationIcon = (source = "") => {
  if (source.includes("job_approval")) return "ri-briefcase-4-line";
  if (source.includes("admin")) return "ri-megaphone-line";
  return "ri-notification-3-line";
};

const NotificationItem = ({ notification, onOpen }) => {
  const url = getNotificationUrl(notification);
  const unread = !notification.readAt;
  const itemClass = `d-block text-decoration-none text-dark border rounded-3 p-3 p-md-4 bg-white ${
    unread ? "border-primary shadow-sm" : "border-light"
  }`;
  const content = (
    <div className="d-flex gap-3 align-items-start">
      <span
        className={`rounded-3 d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
          unread ? "bg-primary text-white" : "bg-light text-secondary"
        }`}
        style={{ width: 44, height: 44 }}
      >
        <i className={`${getNotificationIcon(notification.source)} fs-5`} />
      </span>

      <div className="flex-grow-1 min-w-0">
        <div className="d-flex justify-content-between align-items-start gap-3">
          <div className="min-w-0">
            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
              <h2 className="h6 fw-bold mb-0 text-break">
                {notification.title || "Notification"}
              </h2>
              {unread ? (
                <span className="badge bg-primary">New</span>
              ) : (
                <span className="badge bg-light text-secondary border">Read</span>
              )}
            </div>
            <div className="text-muted text-break">
              {notification.body || "No message available."}
            </div>
          </div>
          {url ? (
            <span className="btn btn-sm btn-light border d-inline-flex align-items-center gap-1 flex-shrink-0">
              Open
              <i className="ri-arrow-right-line" />
            </span>
          ) : null}
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap small text-muted mt-3">
          <span className="d-inline-flex align-items-center gap-1">
            <i className="ri-time-line" />
            {formatDateTime(notification.createdAt) || "Just now"}
          </span>
          {notification.source ? (
            <span className="d-inline-flex align-items-center gap-1">
              <i className="ri-price-tag-3-line" />
              {notification.source.replaceAll("_", " ")}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );

  if (url && isExternalUrl(url)) {
    return (
      <a
        className={itemClass}
        href={url}
        target="_blank"
        rel="noreferrer"
        onClick={() => onOpen(notification)}
      >
        {content}
      </a>
    );
  }

  if (url) {
    return (
      <Link className={itemClass} to={url} onClick={() => onOpen(notification)}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={`${itemClass} text-start w-100`}
      style={{ cursor: unread ? "pointer" : "default" }}
      onClick={() => onOpen(notification)}
    >
      {content}
    </button>
  );
};

const MyNotificationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [serverTotalCount, setServerTotalCount] = useState(0);
  const [serverReadCount, setServerReadCount] = useState(0);

  const readCount = useMemo(
    () => serverReadCount || Math.max(notifications.length - unreadCount, 0),
    [notifications.length, serverReadCount, unreadCount]
  );

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.get("/notifications", authConfig);
      const responseData = response.data?.data || {};
      const meta = response.data?.meta || {};
      const nextNotifications = responseData.notifications || [];

      setNotifications(nextNotifications);
      setUnreadCount(responseData.unreadCount || 0);
      setServerTotalCount(meta.total ?? nextNotifications.length);
      setServerReadCount(
        Math.max((meta.total ?? nextNotifications.length) - (responseData.unreadCount || 0), 0)
      );
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAllRead = async () => {
    try {
      setMarking(true);
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.put("/notifications/read", {}, authConfig);
      const responseData = response.data?.data || {};
      setUnreadCount(responseData.unreadCount || 0);
      setServerTotalCount(responseData.totalCount ?? notifications.length);
      setServerReadCount(responseData.readCount ?? notifications.length);
      await fetchNotifications();
      toast.success("Notifications marked as read");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update notifications");
    } finally {
      setMarking(false);
    }
  };

  const markNotificationRead = async (notification) => {
    if (!notification?.id || notification.readAt) return;

    const readAt = new Date().toISOString();
    setNotifications((current) =>
      current.map((item) =>
        item.id === notification.id ? { ...item, readAt } : item
      )
    );
    setUnreadCount((current) => Math.max(current - 1, 0));

    try {
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.put(
        "/notifications/read",
        { ids: [notification.id] },
        authConfig
      );
      const responseData = response.data?.data || {};
      setUnreadCount(responseData.unreadCount ?? 0);
      setServerTotalCount(responseData.totalCount ?? notifications.length);
      setServerReadCount(responseData.readCount ?? readCount + 1);
    } catch (error) {
      setNotifications((current) =>
        current.map((item) =>
          item.id === notification.id ? { ...item, readAt: null } : item
        )
      );
      setUnreadCount((current) => current + 1);
      toast.error(error.response?.data?.message || "Unable to mark notification read");
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
            <div>
              <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill bg-primary-subtle text-primary fw-semibold small mb-3">
                <i className="ri-notification-3-line" />
                Notification Inbox
              </div>
              <h1 className="h3 fw-bold mb-1">Notifications</h1>
              <p className="text-muted mb-0">
                Keep track of job updates, broadcasts, and account alerts in one
                place.
              </p>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-light border d-inline-flex align-items-center gap-2"
                disabled={loading}
                onClick={fetchNotifications}
              >
                <i className="ri-refresh-line" />
                Refresh
              </button>
              <button
                type="button"
                className="btn btn-primary d-inline-flex align-items-center gap-2"
                disabled={marking || unreadCount === 0}
                onClick={markAllRead}
              >
                <i className="ri-check-double-line" />
                {marking ? "Updating..." : "Mark all read"}
              </button>
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body d-flex align-items-center gap-3">
                  <span
                    className="rounded-3 bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center"
                    style={{ width: 44, height: 44 }}
                  >
                    <i className="ri-inbox-line fs-5" />
                  </span>
                  <div>
                    <div className="h5 mb-0">
                      {serverTotalCount || notifications.length}
                    </div>
                    <div className="text-muted small">Total</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body d-flex align-items-center gap-3">
                  <span
                    className="rounded-3 bg-warning-subtle text-warning d-inline-flex align-items-center justify-content-center"
                    style={{ width: 44, height: 44 }}
                  >
                    <i className="ri-flashlight-line fs-5" />
                  </span>
                  <div>
                    <div className="h5 mb-0">{unreadCount}</div>
                    <div className="text-muted small">Unread</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body d-flex align-items-center gap-3">
                  <span
                    className="rounded-3 bg-success-subtle text-success d-inline-flex align-items-center justify-content-center"
                    style={{ width: 44, height: 44 }}
                  >
                    <i className="ri-checkbox-circle-line fs-5" />
                  </span>
                  <div>
                    <div className="h5 mb-0">{readCount}</div>
                    <div className="text-muted small">Read</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body p-3 p-md-4">
              {loading ? (
                <div className="py-5 text-center text-muted">
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                    aria-hidden="true"
                  />
                  <div className="fw-semibold">Loading notifications...</div>
                </div>
              ) : notifications.length ? (
                <div className="d-grid gap-3">
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onOpen={markNotificationRead}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-5 text-center">
                  <span
                    className="rounded-circle bg-light text-secondary d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: 72, height: 72 }}
                  >
                    <i className="ri-notification-off-line fs-2" />
                  </span>
                  <h2 className="h5 fw-bold mb-1">No notifications yet</h2>
                  <p className="text-muted mb-0">
                    New job updates and admin broadcasts will appear here.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default MyNotificationsPage;
